/**
 * Auditoria 4 - Stage 4: Picker de fecha/hora en UTC.
 * Reemplaza <input type="datetime-local"> nativo (formato local del navegador, ambiguo)
 * por un shadcn Calendar + selector HH:MM que siempre interpreta UTC.
 *
 * Uso:
 *   <UTCDateTimePicker value={iso} onChange={(iso) => setEtr(iso)} />
 *
 * value: string ISO-8601 en UTC (o "").
 * onChange: recibe siempre ISO-8601 UTC (yyyy-MM-ddTHH:mm:00.000Z) o "" si limpia.
 */
import { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Calendar } from './calendar';
import { Input } from './input';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';

function pad(n) { return String(n).padStart(2, '0'); }

function parseISO(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d;
}

function toDisplay(iso) {
    const d = parseISO(iso);
    if (!d) return 'Selecciona fecha y hora UTC';
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

export function UTCDateTimePicker({ value, onChange, disabled = false, testid = 'utc-datetime' }) {
    const parsed = useMemo(() => parseISO(value), [value]);
    // Calendar espera Date en local; construimos un Date local que "coincida" con Y/M/D UTC
    const calendarDate = parsed
        ? new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
        : undefined;
    const [timeStr, setTimeStr] = useState(parsed ? `${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}` : '12:00');

    // Mantener timeStr sincronizado cuando el prop value cambia (ej. dialog reopen con nuevo valor)
    useEffect(() => {
        if (parsed) setTimeStr(`${pad(parsed.getUTCHours())}:${pad(parsed.getUTCMinutes())}`);
    }, [value, parsed]);

    const emit = (date, tStr) => {
        if (!date) { onChange(''); return; }
        const [hh, mm] = (tStr || '00:00').split(':').map(x => parseInt(x, 10) || 0);
        // Construimos Date UTC explicito
        const iso = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hh, mm, 0, 0)).toISOString();
        onChange(iso);
    };

    const onSelectDate = (date) => {
        if (!date) return;
        emit(date, timeStr);
    };

    const onChangeTime = (e) => {
        const t = e.target.value;
        setTimeStr(t);
        if (calendarDate) emit(calendarDate, t);
    };

    return (
        <div className="flex items-center gap-2">
            <Popover>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        className="justify-start text-left font-normal flex-1"
                        data-testid={`${testid}-trigger`}
                    >
                        <CalendarIcon className="w-4 h-4 mr-2 text-primary" />
                        <span className={parsed ? '' : 'text-muted-foreground'}>{toDisplay(value)}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={calendarDate}
                        onSelect={onSelectDate}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
            <div className="flex items-center gap-1 shrink-0">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <Input
                    type="time"
                    value={timeStr}
                    onChange={onChangeTime}
                    disabled={disabled || !calendarDate}
                    className="w-[110px]"
                    data-testid={`${testid}-time`}
                />
            </div>
        </div>
    );
}

export default UTCDateTimePicker;
